/**
 * pageSeoAudit.ts
 *
 * File purpose:
 * - Provide a ready-to-use, page-by-page SEO audit dataset with exact Title, Meta, H1,
 *   primary/secondary keywords, canonical slug and H2/H3 outlines for each public page.
 * - This data is safe to import into components or used by developers to update Seo props
 *   across the site while preserving existing visual design.
 *
 * Notes:
 * - Keep this file as content-only guidance. It does not mutate UI or routing.
 * - All strings follow recommended lengths (titles ~50–60 chars, metas 100–160 chars).
 */

/**
 * FAQItem
 * - Structure for a single question/answer pair intended for JSON-LD FAQ schema.
 */
export interface FAQItem {
  /** Question text */
  question: string
  /** Short, direct answer text (suitable for featured snippets) */
  answer: string
}

/**
 * PageSeo
 * - Structure describing SEO recommendations for a single page.
 *
 * Added:
 * - Optional `faqs` property to hold short Q&A pairs that can be emitted as FAQ JSON-LD.
 */
export interface PageSeo {
  /** Short recommended title (50–60 chars) */
  title: string
  /** Meta description (100–160 chars) */
  meta: string
  /** Exact H1 to use on the page (one only) */
  h1: string
  /** Primary search keyword (short) */
  primaryKeyword: string
  /** Secondary keywords (array) */
  secondaryKeywords: string[]
  /** Canonical URL slug (relative) */
  slug: string
  /** Recommended schema types to include (e.g., Organization, Service, FAQ) */
  schemas: string[]
  /** H2/H3 outline: array of H2 blocks; each contains H3 suggestions (1–3) */
  outline: Array<{
    h2: string
    h3?: string[]
  }>
  /** Recommended approximate content length (words) */
  contentLengthWords: string
  /** Optional FAQ pairs to generate JSON-LD for this page (helps Google & AI answers) */
  faqs?: FAQItem[]
}

/**
 * pageSeoAudit
 * - Complete mapping of public pages -> PageSeo recommendations.
 *
 * Notes:
 * - High-value pages include short, direct FAQs to improve AI-readability and chance of
 *   appearing in rich results. Answers are concise (suitable for featured snippets).
 */
export const pageSeoAudit: Record<string, PageSeo> = {
  home: {
    title: 'Edicoo — Full‑Service Advertising & Media Planning Agency (Iraq)',
    meta:
      'Edicoo: media planning, digital advertising, influencer marketing & creative services across Iraq & Kurdistan. Trusted local agency with 15+ years’ experience.',
    h1: 'Edicoo — Advertising & Media Planning Agency in Iraq',
    primaryKeyword: 'advertising agency Iraq',
    secondaryKeywords: ['media planning Iraq', 'digital marketing Iraq', 'Erbil advertising agency'],
    slug: '/',
    schemas: ['Organization', 'FAQ', 'WebSite', 'LocalBusiness (snippet for homepage)'],
    outline: [
      { h2: 'What We Do', h3: ['Media planning & buying', 'Digital advertising', 'Influencer marketing'] },
      { h2: 'Why Edicoo', h3: ['15+ years local experience', 'Case highlights', 'Client logos'] },
      { h2: 'Featured Work & Proof', h3: ['Selected case studies', 'Measured results (KPIs)', 'Testimonials'] }
    ],
    contentLengthWords: '700–1,200',
    faqs: [
      {
        question: 'Where is Edicoo located?',
        answer: 'Edicoo is based in Erbil and serves clients across Iraq and Kurdistan with local offices and remote services.'
      },
      {
        question: 'What services does Edicoo offer?',
        answer: 'We provide media planning & buying, digital marketing, influencer campaigns, content production and campaign monitoring.'
      },
      {
        question: 'How long has Edicoo been operating?',
        answer: 'Edicoo has over 15 years of experience delivering media and advertising services in Iraq.'
      }
    ]
  },

  about: {
    title: 'About Edicoo — Iraq’s Media & Advertising Experts',
    meta:
      'Learn how Edicoo has delivered media planning, digital ads and influencer campaigns across Iraq & Kurdistan since 2008. Meet the team and see our results.',
    h1: 'About Edicoo — Media & Advertising Experts in Iraq',
    primaryKeyword: 'about edicoo advertising agency',
    secondaryKeywords: ['edicoo team', 'advertising agency history iraq'],
    slug: '/about',
    schemas: ['Organization', 'Person (leadership)', 'FAQ'],
    outline: [
      { h2: 'Our Story', h3: ['Founding & mission', 'Growth timeline'] },
      { h2: 'What We Offer', h3: ['Services overview', 'Local market expertise'] },
      { h2: 'Team & Expertise', h3: ['Leadership bios', 'Years of experience'] }
    ],
    contentLengthWords: '600–1,000'
  },

  services: {
    title: 'Services — Media Planning, Digital Marketing & Influencer Campaigns',
    meta:
      'End‑to‑end services: media planning & buying, digital ads, influencer marketing, content production and campaign monitoring across Iraq & Kurdistan.',
    h1: 'Our Services — Media Planning & Digital Marketing',
    primaryKeyword: 'media planning services iraq',
    secondaryKeywords: ['digital marketing iraq', 'influencer marketing iraq'],
    slug: '/services',
    schemas: ['Service', 'FAQ', 'BreadcrumbList'],
    outline: [
      { h2: 'Digital Marketing', h3: ['Channels we manage', 'Performance approach'] },
      { h2: 'Media Planning & Buying', h3: ['Traditional & digital mix', 'Budget optimization'] },
      { h2: 'Creative & Monitoring', h3: ['Content production', 'Campaign tracking & reporting'] }
    ],
    contentLengthWords: '800–1,200',
    faqs: [
      {
        question: 'Can Edicoo manage both digital and traditional media?',
        answer: 'Yes — Edicoo delivers integrated campaigns across digital channels and traditional media like TV, radio and OOH.'
      },
      {
        question: 'Do you provide campaign reporting?',
        answer: 'We provide regular performance reports with KPIs, insights and optimization recommendations.'
      },
      {
        question: 'How does Edicoo choose influencers?',
        answer: 'We select influencers based on audience fit, verified engagement and campaign objectives to ensure measurable results.'
      }
    ]
  },

  'services/digital-marketing': {
    title: 'Digital Marketing in Iraq — Edicoo',
    meta:
      'Data‑driven digital marketing: social, search, programmatic & analytics — tailored for Iraqi & Kurdish audiences to drive measurable growth.',
    h1: 'Digital Marketing Services in Iraq',
    primaryKeyword: 'digital marketing iraq',
    secondaryKeywords: ['social media iraq', 'google ads iraq', 'programmatic iraq'],
    slug: '/services/digital-marketing',
    schemas: ['Service', 'FAQ', 'BreadcrumbList'],
    outline: [
      { h2: 'What We Do', h3: ['Paid ads', 'Organic strategy', 'Analytics & tracking'] },
      { h2: 'How It Works', h3: ['Audience research', 'Creative & testing'] },
      { h2: 'Results & Case Example', h3: ['KPI examples', 'Client snapshot'] }
    ],
    contentLengthWords: '800–1,200'
  },

  'services/media-planning': {
    title: 'Media Planning & Buying Agency in Iraq',
    meta:
      'Strategic media planning and buying across TV, radio, OOH and print. We optimize reach, timing and budget for Iraq & Kurdistan campaigns.',
    h1: 'Media Planning & Buying in Iraq (Erbil & Baghdad)',
    primaryKeyword: 'media planning agency iraq',
    secondaryKeywords: ['tv media buying iraq', 'ooh advertising iraq'],
    slug: '/services/media-planning',
    schemas: ['Service', 'LocalBusiness (regionally)'],
    outline: [
      { h2: 'Strategy & Research', h3: ['Audience mapping', 'Channel selection'] },
      { h2: 'Buying & Negotiation', h3: ['Rate negotiation', 'Premium placements'] },
      { h2: 'Reporting & Optimization', h3: ['Post-campaign analysis', 'Continuous improvements'] }
    ],
    contentLengthWords: '900–1,500'
  },

  'services/influencer-marketing': {
    title: 'Influencer Marketing in Iraq — Edicoo',
    meta:
      'Influencer selection, contracts, content creation and measurement across Kurdish and Arabic audiences. Verified engagement and campaign forecasts.',
    h1: 'Influencer Marketing for Iraqi Audiences',
    primaryKeyword: 'influencer marketing iraq',
    secondaryKeywords: ['kurdish influencers', 'arab influencers', 'influencer agency iraq'],
    slug: '/services/influencer-marketing',
    schemas: ['Service', 'FAQ', 'BreadcrumbList'],
    outline: [
      { h2: 'Selection Methodology', h3: ['Audience fit', 'Engagement verification'] },
      { h2: 'Campaign Execution', h3: ['Contracts & briefs', 'Content approval'] },
      { h2: 'Measurement', h3: ['Reach & engagement KPIs', 'Reporting cadence'] }
    ],
    contentLengthWords: '700–1,200'
  },

  'services/paid-advertising': {
    title: 'Paid Advertising — Google, Meta, TikTok & X Ads',
    meta:
      'Performance-focused paid advertising across Google, Meta, TikTok and X. Targeted creative, conversion tracking and continuous optimization for ROI.',
    h1: 'Paid Advertising & Performance Marketing',
    primaryKeyword: 'paid advertising iraq',
    secondaryKeywords: ['google ads iraq', 'meta ads iraq', 'tiktok ads iraq'],
    slug: '/services/paid-advertising',
    schemas: ['Service', 'FAQ'],
    outline: [
      { h2: 'Platforms We Cover', h3: ['Google Ads', 'Meta', 'TikTok & X'] },
      { h2: 'Strategy & Creative', h3: ['Audience funnels', 'Creative testing'] },
      { h2: 'Optimization & Reporting', h3: ['Bid strategies', 'Conversion tracking'] }
    ],
    contentLengthWords: '600–1,000'
  },

  'services/social-media': {
    title: 'Social Media Management in Iraq — Edicoo',
    meta:
      'Strategy, content creation and community management to grow brands across Facebook, Instagram, TikTok and LinkedIn in Iraq & Kurdistan.',
    h1: 'Social Media Management & Content Strategy',
    primaryKeyword: 'social media management iraq',
    secondaryKeywords: ['social strategy iraq', 'community management iraq'],
    slug: '/services/social-media',
    schemas: ['Service', 'FAQ'],
    outline: [
      { h2: 'Strategy & Planning', h3: ['Content calendars', 'Tone of voice'] },
      { h2: 'Content Production', h3: ['Video, graphics, copy'] },
      { h2: 'Community & Reporting', h3: ['Moderation', 'Analytics'] }
    ],
    contentLengthWords: '600–1,000'
  },

  'services/content-creation': {
    title: 'Creative Content Production — Video, Graphics & Copy',
    meta:
      'Full creative production: video, motion, graphics and copy optimized for Iraqi audiences and campaign goals.',
    h1: 'Content Creation & Production',
    primaryKeyword: 'content creation iraq',
    secondaryKeywords: ['video production iraq', 'motion graphics iraq'],
    slug: '/services/content-creation',
    schemas: ['Service'],
    outline: [
      { h2: 'Video Production', h3: ['Pre-production', 'Shooting & post'] },
      { h2: 'Design & Motion', h3: ['Graphics', 'Motion templates'] },
      { h2: 'Copy & Localization', h3: ['Ad copy', 'Localized messaging'] }
    ],
    contentLengthWords: '500–900'
  },

  'services/translation': {
    title: 'Translation & Cultural Adaptation for Iraqi Markets',
    meta:
      'Localized translation and cultural adaptation (Arabic, Kurdish, Turkish, English) with Turquality support and marketing localization.',
    h1: 'Translation & Cultural Adaptation',
    primaryKeyword: 'translation services iraq',
    secondaryKeywords: ['kurdish translation', 'arabic localization'],
    slug: '/services/translation',
    schemas: ['Service'],
    outline: [
      { h2: 'Languages & Expertise', h3: ['Arabic', 'Kurdish (Sorani/Badini)', 'Turkish & English'] },
      { h2: 'Process & QA', h3: ['Adaptation', 'Legal & Turquality support'] },
      { h2: 'Use Cases', h3: ['Ads', 'Scripts', 'Product copy'] }
    ],
    contentLengthWords: '400–800'
  },

  'services/tv-advertising': {
    title: 'TV Advertising & Commercial Production in Iraq',
    meta:
      'TV campaign planning, production and buying across national and regional channels. Creative production and performance tracking included.',
    h1: 'TV Advertising & Production',
    primaryKeyword: 'tv advertising iraq',
    secondaryKeywords: ['tv production iraq', 'tv media buying iraq'],
    slug: '/services/tv-advertising',
    schemas: ['Service', 'FAQ'],
    outline: [
      { h2: 'Planning & Scheduling', h3: ['Target slots', 'Audience reach'] },
      { h2: 'Production', h3: ['Creative concept', 'Post-production'] },
      { h2: 'Buying & Tracking', h3: ['Negotiation', 'Spot delivery reports'] }
    ],
    contentLengthWords: '600–1,200'
  },

  'services/ooh-advertising': {
    title: 'Out-of-Home Advertising — Billboards & Transit (Iraq)',
    meta:
      'Strategic OOH placements, transit and digital billboards to maximize visibility in key Iraqi markets with location analytics.',
    h1: 'Out‑of‑Home (OOH) Advertising',
    primaryKeyword: 'ooh advertising iraq',
    secondaryKeywords: ['billboards iraq', 'transit advertising iraq'],
    slug: '/services/ooh-advertising',
    schemas: ['Service', 'LocalBusiness (city targeting)'],
    outline: [
      { h2: 'Formats & Locations', h3: ['Billboards', 'Transit & airports'] },
      { h2: 'Placement Strategy', h3: ['Footfall analytics', 'Audience mapping'] },
      { h2: 'Campaign Examples', h3: ['Case highlights', 'Visibility metrics'] }
    ],
    contentLengthWords: '500–900'
  },

  'services/radio-advertising': {
    title: 'Radio Advertising & Audio Production in Iraq',
    meta:
      'Targeted radio campaigns, scriptwriting and voice talent to reach local audiences during prime listening times.',
    h1: 'Radio Advertising & Production',
    primaryKeyword: 'radio advertising iraq',
    secondaryKeywords: ['audio production iraq', 'radio campaigns iraq'],
    slug: '/services/radio-advertising',
    schemas: ['Service', 'FAQ'],
    outline: [
      { h2: 'Creative Production', h3: ['Scriptwriting', 'Voice talent & studio'] },
      { h2: 'Station Selection', h3: ['Demographics & timing'] },
      { h2: 'Measurement', h3: ['Reach estimates', 'Post-campaign reports'] }
    ],
    contentLengthWords: '400–800'
  },

  'services/print-advertising': {
    title: 'Print Advertising — Newspapers & Magazines (Iraq)',
    meta:
      'Creative print campaigns and premium placements in leading Iraqi newspapers and magazines with production proofs and distribution.',
    h1: 'Print Advertising & Premium Placements',
    primaryKeyword: 'print advertising iraq',
    secondaryKeywords: ['newspaper ads iraq', 'magazine advertising iraq'],
    slug: '/services/print-advertising',
    schemas: ['Service'],
    outline: [
      { h2: 'Creative & Layout', h3: ['Design specs', 'Color proofing'] },
      { h2: 'Media Selection', h3: ['Newspapers', 'Trade magazines'] },
      { h2: 'Delivery & Tracking', h3: ['Distribution summary', 'Ad performance'] }
    ],
    contentLengthWords: '400–800'
  },

  'services/monitoring': {
    title: 'Media Monitoring & Campaign Tracking in Iraq',
    meta:
      'Real‑time monitoring, TV clipping and cross‑channel campaign tracking with actionable reports and alerts to optimize campaigns.',
    h1: 'Monitoring & Campaign Tracking',
    primaryKeyword: 'media monitoring iraq',
    secondaryKeywords: ['tv monitoring iraq', 'campaign tracking iraq'],
    slug: '/services/monitoring',
    schemas: ['Service', 'SoftwareApplication (dashboard)'],
    outline: [
      { h2: 'Real‑Time Monitoring', h3: ['TV channels', 'Radio & OOH mentions'] },
      { h2: 'Analytics & Alerts', h3: ['Dashboards', 'Custom reports'] },
      { h2: 'How We Use Data', h3: ['Optimization loops', 'Client reporting'] }
    ],
    contentLengthWords: '500–900'
  },

  'services/dubbing': {
    title: 'Dubbing & AV Localization Services (Arabic & Kurdish)',
    meta:
      'Professional dubbing and localization services for video content, ensuring cultural and linguistic accuracy for Iraqi audiences.',
    h1: 'Dubbing & Audio‑Visual Localization',
    primaryKeyword: 'dubbing services iraq',
    secondaryKeywords: ['audio localization iraq', 'video dubbing iraq'],
    slug: '/services/dubbing',
    schemas: ['Service'],
    outline: [
      { h2: 'Studio & Talent', h3: ['Voice casting', 'Recording process'] },
      { h2: 'Localization Process', h3: ['Transcreation', 'Sync & mix'] },
      { h2: 'Samples & Delivery', h3: ['Language options', 'File formats'] }
    ],
    contentLengthWords: '400–800'
  },

  team: {
    title: 'Our Team — Edicoo Leadership & Specialists',
    meta:
      "Meet Edicoo's leadership and specialist teams in media planning, digital and creative. LinkedIn profiles and expertise for each member.",
    h1: 'Our Team',
    primaryKeyword: 'edicoo team',
    secondaryKeywords: ['agency team iraq', 'media team erbil'],
    slug: '/team',
    schemas: ['Person (multiple)', 'Organization'],
    outline: [
      { h2: 'Leadership & Key Contacts', h3: ['Agency Manager', 'Directors'] },
      { h2: 'Our Specialists', h3: ['Media planning', 'Digital & creative'] },
      { h2: 'Careers & Contact', h3: ['Open roles', 'How to contact'] }
    ],
    contentLengthWords: '400–800'
  },

  portfolio: {
    title: 'Portfolio — Client Case Studies & Campaigns',
    meta:
      'Explore Edicoo’s client portfolio: media planning, influencer campaigns, TV and digital case studies with results and learnings.',
    h1: 'Client Portfolio & Case Studies',
    primaryKeyword: 'advertising portfolio iraq',
    secondaryKeywords: ['case studies iraq', 'client work edicoo'],
    slug: '/portfolio',
    schemas: ['CollectionPage', 'CreativeWork', 'Organization'],
    outline: [
      { h2: 'Featured Case Studies', h3: ['Summary cards', 'Key metrics'] },
      { h2: 'Client Logos & Sectors', h3: ['Industries served', 'Partnership length'] },
      { h2: 'Request Case Study', h3: ['Contact for similar work', 'Download PDF'] }
    ],
    contentLengthWords: '600–1,200',
    faqs: [
      {
        question: 'Can I see campaign results or KPIs?',
        answer: 'Yes — selected case studies include KPIs, reach metrics and campaign learnings in each portfolio entry.'
      },
      {
        question: 'Can Edicoo share references?',
        answer: 'We provide client references and detailed case studies upon request and with client permission.'
      }
    ]
  },

  'portfolio/[client]': {
    title: '[Client Name] — Campaign Case Study | Edicoo',
    meta:
      'Case study: objective, approach, channels and measured results. See how Edicoo delivered measurable outcomes for this client.',
    h1: '[Client Name] — Case Study',
    primaryKeyword: 'client case study iraq',
    secondaryKeywords: ['campaign results', 'media case study'],
    slug: '/portfolio/:clientId',
    schemas: ['CreativeWork', 'Report', 'VideoObject (if applicable)'],
    outline: [
      { h2: 'Client Brief & Objective', h3: ['Business goal', 'Target audience'] },
      { h2: 'Strategy & Execution', h3: ['Media mix', 'Creative approach'] },
      { h2: 'Results & Learnings', h3: ['KPIs', 'Visual assets & embeds'] }
    ],
    contentLengthWords: '700–1,500'
  },

  contact: {
    title: 'Contact Edicoo — Request Proposal & Office Info',
    meta:
      'Contact Edicoo for media planning, influencer campaigns, production and digital marketing. Office in Erbil — phone, map and quick response promise.',
    h1: 'Get in Touch',
    primaryKeyword: 'contact edicoo',
    secondaryKeywords: ['edicoo phone', 'edicoo office erbil'],
    slug: '/contact',
    schemas: ['ContactPoint', 'FAQ', 'LocalBusiness (contact)'],
    outline: [
      { h2: 'Contact Form', h3: ['What to include', 'Response time promise'] },
      { h2: 'Office & Phone', h3: ['Address', 'Map embed'] },
      { h2: 'Quick Response Promise', h3: ['SLA', 'Emergency contacts'] }
    ],
    contentLengthWords: '300–600',
    faqs: [
      {
        question: 'How quickly will Edicoo respond to inquiries?',
        answer: 'We typically respond within 24–48 business hours; urgent requests may receive an accelerated response.'
      },
      {
        question: 'Where is your Erbil office?',
        answer: 'Our primary office is located in Erbil — full address and map are available on the Contact page.'
      },
      {
        question: 'Can I request a proposal online?',
        answer: 'Yes — use the contact form to request a proposal and include campaign goals, timelines and budgets for a faster reply.'
      }
    ]
  },

  terms: {
    title: 'Terms of Service — Edicoo',
    meta: 'Edicoo Terms of Service. Legal terms governing use of the website and our service engagements.',
    h1: 'Terms of Service',
    primaryKeyword: 'edicoo terms of service',
    secondaryKeywords: ['terms of service iraq', 'agency terms'],
    slug: '/terms',
    schemas: ['WebPage'],
    outline: [
      { h2: 'Use of Our Services', h3: ['Acceptable use', 'Accountability'] },
      { h2: 'Payments & Fees', h3: ['Billing', 'Media budgets'] },
      { h2: 'Limitation of Liability', h3: ['Governing law', 'Dispute resolution'] }
    ],
    contentLengthWords: 'N/A (legal text)'
  },

  privacy: {
    title: 'Privacy Policy — Edicoo',
    meta:
      'Edicoo Advertising & Media Agency privacy policy: what we collect, how we use it and how to contact us for data requests.',
    h1: 'Privacy Policy',
    primaryKeyword: 'edicoo privacy policy',
    secondaryKeywords: ['data protection iraq', 'privacy edicoo'],
    slug: '/privacy',
    schemas: ['WebPage'],
    outline: [
      { h2: 'Information We Collect', h3: ['Contact data', 'Usage data', 'Project files'] },
      { h2: 'How We Use Data', h3: ['Service delivery', 'Marketing'] },
      { h2: 'Your Rights', h3: ['Access', 'Correction', 'Deletion'] }
    ],
    contentLengthWords: 'N/A (legal text)'
  },

  'landings/erbil': {
    title: 'Edicoo — Media Planning & Advertising Agency in Erbil',
    meta:
      'Local Edicoo office in Erbil providing media planning, digital marketing, influencer partnerships and creative services tailored to Erbil audiences.',
    h1: 'Media Planning Agency in Erbil',
    primaryKeyword: 'erbil advertising agency',
    secondaryKeywords: ['media planning erbil', 'advertising erbil iraq'],
    slug: '/landings/erbil',
    schemas: ['LocalBusiness', 'FAQ', 'Place'],
    outline: [
      { h2: 'Local Services', h3: ['Media planning', 'Local influencer sourcing'] },
      { h2: 'Office & Contact', h3: ['Address', 'Phone & hours'] },
      { h2: 'Local Case Studies', h3: ['Erbil campaigns', 'Results'] }
    ],
    contentLengthWords: '400–800',
    faqs: [
      {
        question: 'Do you offer local influencer campaigns in Erbil?',
        answer: 'Yes — we source Kurdish and local influencers and manage end-to-end campaigns specifically for Erbil audiences.'
      },
      {
        question: 'Can you run on-ground production in Erbil?',
        answer: 'We provide local production services including shoots, talent and logistics for campaigns in Erbil.'
      }
    ]
  },

  'landings/baghdad': {
    title: 'Edicoo — Baghdad Advertising & Digital Marketing',
    meta:
      'Edicoo delivers media planning, digital advertising and influencer campaigns across Baghdad and central Iraq with local production support.',
    h1: 'Edicoo — Baghdad Advertising & Digital Marketing',
    primaryKeyword: 'baghdad advertising agency',
    secondaryKeywords: ['media planning baghdad', 'advertising baghdad'],
    slug: '/landings/baghdad',
    schemas: ['LocalBusiness', 'FAQ', 'Place'],
    outline: [
      { h2: 'Services in Baghdad', h3: ['Campaign execution', 'On‑ground production'] },
      { h2: 'Contact & Area Served', h3: ['Phone', 'Service area'] },
      { h2: 'Local Case Examples', h3: ['Baghdad projects', 'Local partners'] }
    ],
    contentLengthWords: '400–800'
  },

  'landings/kurdistan': {
    title: 'Edicoo — Kurdistan Advertising & Digital Marketing',
    meta:
      'Regional Edicoo services in Kurdistan: media planning, influencer marketing and digital campaigns tailored for Kurdish audiences.',
    h1: 'Edicoo — Kurdistan Advertising & Digital Marketing',
    primaryKeyword: 'kurdistan advertising agency',
    secondaryKeywords: ['erbil agency', 'kurdish influencer marketing'],
    slug: '/landings/kurdistan',
    schemas: ['Place', 'LocalBusiness', 'FAQ'],
    outline: [
      { h2: 'Regional Services', h3: ['Kurdish-language campaigns', 'Local influencer coordination'] },
      { h2: 'Where We Operate', h3: ['Erbil & surrounding governorates'] },
      { h2: 'Case Studies', h3: ['Regional campaigns', 'Metrics'] }
    ],
    contentLengthWords: '400–800'
  }
}
